import React, { FC, useCallback, useContext, useState } from 'react'
import IFormValues, { Validate } from '../../../validator/IFormValues'
import { FormPhoneFieldTemplate } from '../FormPhoneFieldTemplate/FormPhoneFieldTemplate'
import {
  PHONE_CALL_CODE_INCORRECT_TEXT,
  PHONE_CALL_CODE_IS_NOT_AVAILABLE,
  // PHONE_CALL_CODE_IS_NOT_AVAILABLE,
  PHONE_SMS_CODE_INCORRECT_TEXT,
} from '../constants'
import { IConfirmedPhone } from './interfaces/FormPhoneFieldInterfaces'
import { LocaleContext } from '../../LocaleContext/LocaleContext'
import { getPhoneNumber } from '../../../utils/common'
import { FRONTEND_ERROR, TO_MANY_REQUESTS_STATUS } from '../../../constants/errors'
import { FormFieldTypes } from '../../../emun/RegistrationFormFieldTypes'
import { usePhoneCheckCallCode } from '../../../api/mutations/usePhoneCheckCallCode'
import { useResetUserPasswordByCallCode } from '../../../api/mutations/useResetUserPasswordByCallCode'
import { useResetUserPasswordBySmsCode } from '../../../api/mutations/useResetUserPasswordBySmsCode'
import { IPhoneCheckCodeData } from '../../../interfaces/IPhoneCheckCodeData'
import { TOO_MANY_REQUESTS } from '../../LogonFormComponent/contsants'
import { usePhoneCheckSmsCode } from '../../../api/mutations/usePhoneCheckSmsCode'

type FormPhoneFieldProps<IInitialValues = IFormValues> = {
  appidQueryParam?: string
  doNotShowBusy?: boolean
  withCode?: boolean
  onlySmsCode?: boolean
  phoneSubmitted?: boolean
  withoutDescription?: boolean
  immediatelyShowCodeInput?: boolean
  initialValues?: IInitialValues
  formQueryParam?: string
  setIsPhoneConfirmed?: (error: boolean) => void
  validate?: Validate
}

export const FormPhoneField: FC<FormPhoneFieldProps> = ({
  appidQueryParam,
  withoutDescription,
  initialValues,
  formQueryParam,
  phoneSubmitted = false,
  immediatelyShowCodeInput = false,
  withCode = false,
  onlySmsCode = false,
  doNotShowBusy = false,
  validate,
  setIsPhoneConfirmed,
}) => {
  const { locale } = useContext(LocaleContext)
  const [previousSendCodePhone, previousSendCodePhoneChange] = useState<string>('')
  const [codeFormError, codeFormErrorChange] = useState<string>('')
  const [isCallCode, isCallCodeChange] = useState<boolean>(true)
  const [isShowCodeRegFormInput, isShowCodeRegFormInputChange] = useState<boolean>(immediatelyShowCodeInput)
  const [phoneIsConfirmed, setPhoneIsConfirmed] = useState<IConfirmedPhone>({
    phone: '',
    isConfirmed: false,
  })
  const resetUserPasswordByCallCode = useResetUserPasswordByCallCode()
  const resetUserPasswordBySmsCode = useResetUserPasswordBySmsCode()
  const phoneCheckCallCode = usePhoneCheckCallCode()
  const phoneCheckSmsCode = usePhoneCheckSmsCode()

  const sendCode = useCallback(
    async (phoneSource: string, isCall?: string) => {
      const phone = getPhoneNumber(phoneSource)

      if (previousSendCodePhone && previousSendCodePhone === phone) {
        if (isCall) {
          await resetUserPasswordByCallCode({
            phone,
            action: 'confirm',
          })

          return
        }
        await resetUserPasswordBySmsCode({
          phone,
          action: 'confirm',
        })

        return
      }

      if (isCall) {
        await resetUserPasswordByCallCode({
          phone,
          action: 'confirm',
        })
        previousSendCodePhoneChange(phone)

        return
      }

      await resetUserPasswordBySmsCode({
        phone,
        action: 'confirm',
      })
      previousSendCodePhoneChange(phone)
    },
    [locale, previousSendCodePhone, resetUserPasswordByCallCode, resetUserPasswordBySmsCode],
  )

  const sendCodeBlur = async (phone: string, smsCode: string) => {
    try {
      let phoneCheckCodeResponse: IPhoneCheckCodeData

      if (isCallCode) {
        phoneCheckCodeResponse = await phoneCheckCallCode({
          phone,
          callCode: smsCode,
        })
      } else {
        phoneCheckCodeResponse = await phoneCheckSmsCode({
          phone,
          smsCode,
        })
      }

      const hasPhoneChangeError = phoneCheckCodeResponse.isConfirmed === false
      const phoneCallCodeNotAvailable = phoneCheckCodeResponse.isCodeAvailable === false

      if (hasPhoneChangeError && phoneCallCodeNotAvailable) {
        codeFormErrorChange(PHONE_CALL_CODE_IS_NOT_AVAILABLE)
      } else if (hasPhoneChangeError && isCallCode) {
        codeFormErrorChange(PHONE_CALL_CODE_INCORRECT_TEXT)
      } else if (hasPhoneChangeError) {
        codeFormErrorChange(PHONE_SMS_CODE_INCORRECT_TEXT)
      } else if (phoneCheckCodeResponse.isConfirmed) {
        setIsPhoneConfirmed?.(false)
        isShowCodeRegFormInputChange(false)
        setPhoneIsConfirmed({ phone, isConfirmed: true })
      }
    } catch (e: any) {
      if (e.response?.request?.status === TO_MANY_REQUESTS_STATUS) {
        codeFormErrorChange?.(TOO_MANY_REQUESTS)
      }
      if (e.response.status === FRONTEND_ERROR && isCallCode) {
        codeFormErrorChange?.(PHONE_CALL_CODE_INCORRECT_TEXT)
      } else if (e.response.status === FRONTEND_ERROR) {
        codeFormErrorChange?.(PHONE_SMS_CODE_INCORRECT_TEXT)
      }
    }
  }

  return (
    <FormPhoneFieldTemplate
      appidQueryParam={appidQueryParam}
      doNotShowBusy={doNotShowBusy}
      withCode={withCode}
      onlySmsCode={onlySmsCode}
      phoneSubmitted={phoneSubmitted}
      withoutDescription={withoutDescription}
      immediatelyShowCodeInput={immediatelyShowCodeInput}
      initialValues={initialValues}
      isCallCode={!!isCallCode}
      codeFormError={codeFormError}
      formQueryParam={formQueryParam}
      codeFormErrorChange={codeFormErrorChange}
      sendCodeBlur={sendCodeBlur}
      codeType={FormFieldTypes.SmsCode}
      validate={validate}
      sendCode={sendCode}
      setIsPhoneConfirmed={setIsPhoneConfirmed}
      isShowCodeRegFormInputChange={isShowCodeRegFormInputChange}
      isShowCodeRegFormInput={isShowCodeRegFormInput}
      phoneIsConfirmed={phoneIsConfirmed}
      setPhoneIsConfirmed={setPhoneIsConfirmed}
      isCallCodeChange={isCallCodeChange}
    />
  )
}
